// Real HTTP adapter for the Patient Inquiry Management API (PRD 12). This is the
// DEFAULT transport; the mock (patient-inquiry.mock.ts) is used only when
// VITE_USE_MOCK_API=true — see patient-inquiry.adapter.ts. Maps the server error
// envelope { error: { code, message, fields, meta } } to PatientInquiryApiError.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';

import {
  PatientInquiryApiError,
  type CreatePatientInquiryReplyInput,
  type ListPatientInquiriesParams,
  type ListPatientInquiriesResult,
  type PatientInquiryApi,
  type PatientInquiryApiErrorCode,
  type PatientInquiryDetail,
  type RevealInquirerPhoneResult,
} from '../types/patient-inquiry.types';

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
    meta?: Record<string, unknown>;
  };
}

function mapError(e: unknown): never {
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  const payload = candidate.response?.data?.error;
  throw new PatientInquiryApiError(
    (payload?.code as PatientInquiryApiErrorCode) ?? 'UNKNOWN',
    payload?.message ?? '알 수 없는 오류가 발생했습니다.',
    { fields: payload?.fields, meta: payload?.meta },
  );
}

export const patientInquiryHttpApi: PatientInquiryApi = {
  async listPatientInquiries(
    params: ListPatientInquiriesParams,
  ): Promise<ListPatientInquiriesResult> {
    try {
      const res = await httpClient.get<ApiEnvelope<ListPatientInquiriesResult>>(
        endpoints.patientInquiries,
        { params },
      );
      const data = res.data?.data;
      if (!data || typeof data !== 'object' || !Array.isArray(data.items)) {
        throw new PatientInquiryApiError('UNKNOWN', '환자 문의 목록을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async getPatientInquiry(id: number): Promise<PatientInquiryDetail> {
    try {
      const res = await httpClient.get<ApiEnvelope<PatientInquiryDetail>>(
        `${endpoints.patientInquiries}/${id}`,
      );
      const data = res.data?.data;
      if (!data || !Array.isArray(data.replies)) {
        throw new PatientInquiryApiError('UNKNOWN', '환자 문의를 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async getInquirerPhone(id: number): Promise<RevealInquirerPhoneResult> {
    try {
      const res = await httpClient.get<ApiEnvelope<RevealInquirerPhoneResult>>(
        `${endpoints.patientInquiries}/${id}/inquirer-phone`,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async replyPatientInquiry(
    id: number,
    input: CreatePatientInquiryReplyInput,
  ): Promise<PatientInquiryDetail> {
    try {
      const res = await httpClient.post<ApiEnvelope<PatientInquiryDetail>>(
        `${endpoints.patientInquiries}/${id}/reply`,
        input,
      );
      const data = res.data?.data;
      if (!data || !Array.isArray(data.replies)) {
        throw new PatientInquiryApiError('UNKNOWN', '환자 문의를 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },
};
